import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import PackageDetailsForm from "@/components/travel/PackageDetailsForm";
import { TravelBnplProviderSelection } from "@/components/travel/BNPLProviderSelection";
import PaymentConfirmation from "@/components/travel/PaymentConfirmation";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MapPin, Plane } from "lucide-react";

// Import the same package data from Travel page
import emiratesLogo from "@/assets/airlines/emirates-logo.png";
import airfranceLogo from "@/assets/airlines/airfrance-logo.png";
import jalLogo from "@/assets/airlines/jal-logo.png";
import dubaiMarinaImg from "@/assets/stay-dubai-marina.jpg";
import santoriniImg from "@/assets/stay-santorini.jpg";
import tokyoImg from "@/assets/stay-tokyo.jpg";
import londonImg from "@/assets/stay-london.jpg";
import parisImg from "@/assets/stay-paris.jpg";
import bangkokImg from "@/assets/stay-bangkok.jpg";
import istanbulImg from "@/assets/stay-istanbul.jpg";
import singaporeImg from "@/assets/stay-singapore.jpg";
import romeImg from "@/assets/stay-rome.jpg";
import maldivesImg from "@/assets/stay-maldives.jpg";
import newyorkImg from "@/assets/stay-newyork.jpg";
import niceImg from "@/assets/stay-nice.jpg";

type BookingStep = "details" | "provider" | "confirmation";

interface BookingData {
  packageId: string;
  startDate: string;
  endDate: string;
  travelers: number;
  rooms: number;
  specialRequests?: string;
  selectedProvider?: string;
}

// Travel packages data - same as in Travel.tsx
const travelPackages = [
  {
    id: "1",
    destination: "Dubai Experience",
    flight: { route: "Dubai → London → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "Marina View Hotel", nights: 4, image: dubaiMarinaImg },
    totalPrice: 4200,
    savings: 400
  },
  {
    id: "2", 
    destination: "Greek Island Getaway",
    flight: { route: "Dubai → Athens → Dubai", carrier: "Air France", logo: airfranceLogo },
    stay: { name: "Santorini Luxury Suite", nights: 5, image: santoriniImg },
    totalPrice: 6000,
    savings: 600
  },
  {
    id: "3",
    destination: "Tokyo Adventure", 
    flight: { route: "Dubai → Tokyo → Dubai", carrier: "JAL", logo: jalLogo },
    stay: { name: "Tokyo Business Hotel", nights: 4, image: tokyoImg },
    totalPrice: 4400,
    savings: 350
  },
  {
    id: "4",
    destination: "London City Break",
    flight: { route: "Dubai → London → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "London Central Hotel", nights: 3, image: londonImg },
    totalPrice: 3800,
    savings: 300
  },
  {
    id: "5",
    destination: "Paris Romance",
    flight: { route: "Dubai → Paris → Dubai", carrier: "Air France", logo: airfranceLogo },
    stay: { name: "Champs-Élysées Suite", nights: 4, image: parisImg },
    totalPrice: 5200,
    savings: 520
  },
  {
    id: "6",
    destination: "Mediterranean Escape",
    flight: { route: "Dubai → Nice → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "Riviera Resort", nights: 5, image: niceImg },
    totalPrice: 5800,
    savings: 450
  },
  {
    id: "7",
    destination: "New York Explorer",
    flight: { route: "Dubai → New York → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "Manhattan Plaza Hotel", nights: 6, image: newyorkImg },
    totalPrice: 7200,
    savings: 800
  },
  {
    id: "8",
    destination: "Bangkok Discovery",
    flight: { route: "Dubai → Bangkok → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "Riverside Bangkok Hotel", nights: 4, image: bangkokImg },
    totalPrice: 3600,
    savings: 280
  },
  {
    id: "9",
    destination: "Istanbul Cultural Tour",
    flight: { route: "Dubai → Istanbul → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "Bosphorus View Hotel", nights: 4, image: istanbulImg },
    totalPrice: 3400,
    savings: 250
  },
  {
    id: "10",
    destination: "Singapore Luxury",
    flight: { route: "Dubai → Singapore → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "Marina Bay Suites", nights: 5, image: singaporeImg },
    totalPrice: 4800,
    savings: 380
  },
  {
    id: "11",
    destination: "Rome Historic Journey",
    flight: { route: "Dubai → Rome → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "Colosseum View Hotel", nights: 4, image: romeImg },
    totalPrice: 4600,
    savings: 320
  },
  {
    id: "12",
    destination: "Maldives Paradise",
    flight: { route: "Dubai → Malé → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "Overwater Villa Resort", nights: 7, image: maldivesImg },
    totalPrice: 8500,
    savings: 900
  }
];

export default function PackageBooking() {
  const { packageId } = useParams<{ packageId: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<BookingStep>("details");
  const [bookingData, setBookingData] = useState<BookingData>({
    packageId: packageId || "",
    startDate: "",
    endDate: "",
    travelers: 1,
    rooms: 1
  });

  // Find the selected package
  const selectedPackage = useMemo(() => {
    return travelPackages.find(pkg => pkg.id === packageId);
  }, [packageId]);

  const handleDetailsComplete = (data: Partial<BookingData>) => {
    setBookingData(prev => ({ ...prev, ...data }));
    setCurrentStep("provider");
  };

  const handleProviderSelection = (provider: string) => {
    setBookingData(prev => ({ ...prev, selectedProvider: provider }));
    setCurrentStep("confirmation");
  };

  const handleConfirmPayment = () => {
    // This will redirect to the BNPL provider's payment page
    console.log("Redirecting to BNPL provider for payment...");
    // In production, this would redirect to the selected provider's checkout
  };

  const goBack = () => {
    if (currentStep === "provider") {
      setCurrentStep("details");
    } else if (currentStep === "confirmation") {
      setCurrentStep("provider");
    } else {
      navigate("/travel");
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case "details":
        return "Travel Details";
      case "provider":
        return "Choose Payment Method";
      case "confirmation":
        return "Confirm Payment";
      default:
        return "Book Package";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="pt-20">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              size="sm"
              onClick={goBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">{getStepTitle()}</h1>
              {selectedPackage ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    <p className="text-lg font-medium text-foreground">{selectedPackage.destination}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Plane className="w-4 h-4 text-muted-foreground" />
                    <p className="text-muted-foreground">{selectedPackage.flight.route}</p>
                  </div>
                  <p className="text-2xl font-bold text-primary">AED {selectedPackage.totalPrice.toLocaleString()}</p>
                </div>
              ) : (
                <p className="text-muted-foreground">Package ID: {packageId}</p>
              )}
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                currentStep === "details" ? "bg-primary text-primary-foreground" : 
                ["provider", "confirmation"].includes(currentStep) ? "bg-green-500 text-white" : "bg-muted text-muted-foreground"
              }`}>
                1
              </div>
              <div className={`h-1 w-16 ${
                ["provider", "confirmation"].includes(currentStep) ? "bg-green-500" : "bg-muted"
              }`} />
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                currentStep === "provider" ? "bg-primary text-primary-foreground" : 
                currentStep === "confirmation" ? "bg-green-500 text-white" : "bg-muted text-muted-foreground"
              }`}>
                2
              </div>
              <div className={`h-1 w-16 ${
                currentStep === "confirmation" ? "bg-green-500" : "bg-muted"
              }`} />
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                currentStep === "confirmation" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}>
                3
              </div>
            </div>
          </div>

          {/* Step Content */}
          <Card className="p-8">
            {currentStep === "details" && (
              <PackageDetailsForm
                packageId={packageId || ""}
                selectedPackage={selectedPackage}
                onComplete={handleDetailsComplete}
                initialData={bookingData}
              />
            )}
            {currentStep === "provider" && (
              <TravelBnplProviderSelection
                orderId={bookingData.packageId}
                amount={selectedPackage?.totalPrice || 0}
                currency="AED"
                customerName=""
                customerEmail=""
              />
            )}
            {currentStep === "confirmation" && (
              <PaymentConfirmation
                bookingData={bookingData}
                onConfirm={handleConfirmPayment}
              />
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}